/**
 * WordPress dependencies
 */
import { ToolsPanel, ToolsPanelItem } from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { stringify } from '@nab/utils';

/**
 * Internal dependencies
 */
import type { ToolsPanelProps } from './types';
import {
	getPreviewValue,
	hasInheritedValue,
	hasUserValue,
} from '../../../../utils/props';
import { AnglePickerControl, UnitWithRangeControl } from './internal';

export function TransformationsPanel( {
	value: props,
	onChange,
}: ToolsPanelProps ): JSX.Element {
	// Translate
	const hasTranslateValue = () => hasUserValue( props.translate );
	const isTranslateShownByDefault = hasInheritedValue( props.translate );
	const translateValue = getPreviewValue( props.translate );
	const parsedTranslateValues = parseTranslate( translateValue );
	const onChangeTranslate =
		( coord: 'x' | 'y' ) => ( newValue?: string | number ) => {
			const updatedParsedValues = {
				...parsedTranslateValues,
				[ coord ]: newValue,
			};
			const { x, y, z } = updatedParsedValues;
			const updatedTranslateValue = composeTranslate( x, y, z );
			onChange( { translate: updatedTranslateValue } );
		};

	// Rotate
	const hasRotateValue = () => hasUserValue( props.rotate );
	const isRotateShownByDefault = hasInheritedValue( props.rotate );
	const parsedRotationValue = parseRotate( getPreviewValue( props.rotate ) );

	// Scale
	const hasScaleValue = () => hasUserValue( props.scale );
	const isScaleShownByDefault = hasInheritedValue( props.scale );
	const parsedScaleValue = parseScale( getPreviewValue( props.scale ) );

	const resetAll = () =>
		onChange( {
			rotate: undefined,
			scale: undefined,
			translate: undefined,
		} );

	return (
		<ToolsPanel
			label={ _x( 'Transformations', 'text', 'nelio-ab-testing' ) }
			resetAll={ resetAll }
		>
			<ToolsPanelItem
				label={ _x( 'Translation', 'text', 'nelio-ab-testing' ) }
				hasValue={ hasTranslateValue }
				onSelect={ () => onChange( { translate: '0 0' } ) }
				onDeselect={ () =>
					onChange( {
						translate: undefined,
					} )
				}
				isShownByDefault={ isTranslateShownByDefault }
			>
				<div className="nab-components-transformations-control">
					<UnitWithRangeControl
						label={ _x(
							'Translate X',
							'text',
							'nelio-ab-testing'
						) }
						units={ [ 'px', 'em', 'rem', '%' ] }
						value={ parsedTranslateValues.x ?? '0' }
						onChange={ onChangeTranslate( 'x' ) }
						min={ -256 }
						max={ 256 }
						initialPosition={ 0 }
						disabled={ props.translate?.type === 'readonly' }
						__next40pxDefaultSize
					/>
					<UnitWithRangeControl
						label={ _x(
							'Translate Y',
							'text',
							'nelio-ab-testing'
						) }
						units={ [ 'px', 'em', 'rem', '%' ] }
						value={ parsedTranslateValues.y ?? '0' }
						onChange={ onChangeTranslate( 'y' ) }
						min={ -256 }
						max={ 256 }
						initialPosition={ 0 }
						disabled={ props.translate?.type === 'readonly' }
						__next40pxDefaultSize
					/>
				</div>
			</ToolsPanelItem>
			<ToolsPanelItem
				label={ _x( 'Rotation', 'text', 'nelio-ab-testing' ) }
				hasValue={ hasRotateValue }
				onSelect={ () => onChange( { rotate: undefined } ) }
				onDeselect={ () =>
					onChange( {
						rotate: undefined,
					} )
				}
				isShownByDefault={ isRotateShownByDefault }
			>
				<AnglePickerControl
					label={ _x( 'Rotation', 'text', 'nelio-ab-testing' ) }
					value={ parsedRotationValue }
					disabled={ props.rotate?.type === 'readonly' }
					onChange={ ( newValue ) =>
						onChange( { rotate: `${ newValue }deg` } )
					}
				/>
			</ToolsPanelItem>
			<ToolsPanelItem
				label={ _x( 'Scale', 'text', 'nelio-ab-testing' ) }
				hasValue={ hasScaleValue }
				onSelect={ () => onChange( { scale: '100%' } ) }
				onDeselect={ () =>
					onChange( {
						scale: undefined,
					} )
				}
				isShownByDefault={ isScaleShownByDefault }
			>
				<UnitWithRangeControl
					label={ _x( 'Scale', 'text', 'nelio-ab-testing' ) }
					units={ [ '%' ] }
					value={ parsedScaleValue ?? '100%' }
					onChange={ ( newValue ) =>
						onChange( { scale: stringify( newValue ) } )
					}
					min={ 0 }
					max={
						stringify( parsedScaleValue ).endsWith( '%' )
							? 200
							: undefined
					}
					initialPosition={ 100 }
					disabled={ props.scale?.type === 'readonly' }
					__next40pxDefaultSize
				/>
			</ToolsPanelItem>
		</ToolsPanel>
	);
}

// =======
// HELPERS
// =======

function parseTranslate( translateValue: string ) {
	const values = translateValue.split( /\s+/ );
	return {
		x: values[ 0 ] || undefined,
		y: values[ 1 ] || undefined,
		z: values[ 2 ] || undefined,
	};
}

function composeTranslate(
	x: string | undefined,
	y: string | undefined,
	z: string | undefined
): string {
	return [ x, y, z ].filter( ( value ) => value !== undefined ).join( ' ' );
}

function parseRotate( rotateValue: string ): number {
	rotateValue = rotateValue.replace( 'z', '' ).replace( 'deg', '' ).trim();
	if ( rotateValue.includes( ' ' ) ) {
		return 0;
	}

	const parsedValue = parseFloat( rotateValue );
	return isNaN( parsedValue ) ? 0 : parsedValue;
}

function parseScale( scaleValue: string ): number | string {
	if ( scaleValue.includes( ' ' ) || isNaN( parseFloat( scaleValue ) ) ) {
		return '100%';
	}

	return scaleValue;
}
